import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Assignment,
  Work,
  CloudQueue,
  Thermostat,
  Add,
  Remove,
} from '@mui/icons-material';
import { API_BASE_URL } from '../services/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface WorkActivity {
  description: string;
  crew: string;
  hoursWorked: number;
  location: string;
  equipment: string[];
}

interface MaterialDelivery {
  material: string;
  quantity: number;
  unit: string;
  supplier: string;
  deliveryTime: string;
}

interface DailyReportFormData {
  projectId: string;
  date: string;
  weather: string;
  temperature: string;
  workActivities: WorkActivity[];
  materialDeliveries: MaterialDelivery[];
  totalWorkers: number;
  equipmentUsed: string[];
  delaysOrIssues: string[];
  notes: string;
  status: string;
}

interface Project {
  _id: string;
  name: string;
}

const DailyReportCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [formData, setFormData] = useState<DailyReportFormData>({
    projectId: '',
    date: new Date().toISOString().split('T')[0],
    weather: 'sunny',
    temperature: '20',
    workActivities: [{
      description: '',
      crew: '',
      hoursWorked: 8,
      location: '',
      equipment: []
    }],
    materialDeliveries: [],
    totalWorkers: 1,
    equipmentUsed: [],
    delaysOrIssues: [],
    notes: '',
    status: 'draft',
  });

  const [errors, setErrors] = useState<any>({});

  const weatherOptions = [
    { value: 'sunny', label: 'Clear' },
    { value: 'partly_cloudy', label: 'Partly Cloudy' },
    { value: 'cloudy', label: 'Cloudy' },
    { value: 'overcast', label: 'Overcast' },
    { value: 'light_rain', label: 'Light Rain' },
    { value: 'heavy_rain', label: 'Heavy Rain' },
    { value: 'drizzle', label: 'Drizzle' },
    { value: 'snow', label: 'Snow' },
    { value: 'sleet', label: 'Sleet' },
    { value: 'fog', label: 'Fog' },
    { value: 'windy', label: 'Windy' },
    { value: 'stormy', label: 'Stormy' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (formData.workActivities.length === 0) {
      newErrors.workActivities = 'At least one work activity is required';
    }

    formData.workActivities.forEach((activity, index) => {
      if (!activity.description.trim()) {
        newErrors[`workActivity_${index}_description`] = 'Work description is required';
      }
      if (!activity.crew.trim()) {
        newErrors[`workActivity_${index}_crew`] = 'Crew information is required';
      }
    });

    if (formData.totalWorkers < 1) {
      newErrors.totalWorkers = 'Total workers must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DailyReportFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof DailyReportFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleWorkActivityChange = (index: number, field: keyof WorkActivity, value: any) => {
    const updatedActivities = [...formData.workActivities];
    updatedActivities[index] = {
      ...updatedActivities[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      workActivities: updatedActivities,
    });
  };

  const addWorkActivity = () => {
    setFormData({
      ...formData,
      workActivities: [
        ...formData.workActivities,
        {
          description: '',
          crew: '',
          hoursWorked: 8,
          location: '',
          equipment: []
        }
      ]
    });
  };

  const removeWorkActivity = (index: number) => {
    if (formData.workActivities.length > 1) {
      const updatedActivities = formData.workActivities.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        workActivities: updatedActivities,
      });
    }
  };

  const addMaterialDelivery = () => {
    setFormData({
      ...formData,
      materialDeliveries: [
        ...formData.materialDeliveries,
        {
          material: '',
          quantity: 0,
          unit: '',
          supplier: '',
          deliveryTime: ''
        }
      ]
    });
  };

  const removeMaterialDelivery = (index: number) => {
    const updatedDeliveries = formData.materialDeliveries.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      materialDeliveries: updatedDeliveries,
    });
  };

  const handleMaterialDeliveryChange = (index: number, field: keyof MaterialDelivery, value: any) => {
    const updatedDeliveries = [...formData.materialDeliveries];
    updatedDeliveries[index] = {
      ...updatedDeliveries[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      materialDeliveries: updatedDeliveries,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const summaryFromActivities = formData.workActivities
        .map((a) => (a.description || '').trim())
        .filter(Boolean)
        .join('; ');

      const temperatureNum = parseFloat(formData.temperature);
      const weather: any = { condition: formData.weather };
      if (Number.isFinite(temperatureNum)) {
        weather.temperature = { high: temperatureNum, low: temperatureNum, unit: 'celsius' };
      }

      const payload: any = {
        project: formData.projectId,
        date: formData.date,
        weather,
        workProgress: { summary: summaryFromActivities },
      };
      if (formData.totalWorkers && formData.totalWorkers > 0) {
        payload.manpower = { totalWorkers: Number(formData.totalWorkers) };
      }
  const response = await fetch(`${API_BASE_URL}/daily-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const status = response.status;
        let serverMessage = `Failed to create daily report (HTTP ${status})`;
        try {
          const errBody = await response.json();
          const fieldErrors: any = {};
          if (Array.isArray(errBody?.errors) && errBody.errors.length) {
            serverMessage = errBody.errors.map((e: any) => e.msg || e.message || e).join('; ');
            // Map backend validation fields to UI inputs
            errBody.errors.forEach((e: any) => {
              const param = e.param || e.path || e.field;
              if (param === 'project') fieldErrors.projectId = e.msg || 'Project is required';
              if (param === 'date') fieldErrors.date = e.msg || 'Date is required';
              if (param === 'weather.condition') fieldErrors.weather = e.msg || 'Weather is required';
              if (param === 'workProgress.summary') fieldErrors.workActivities = e.msg || 'Work summary is required';
            });
          } else if (errBody?.message) {
            serverMessage = errBody.message;
            // Heuristic mapping for common messages
            if (/Weather condition is required/i.test(serverMessage)) fieldErrors.weather = 'Weather is required';
            if (/Work summary is required/i.test(serverMessage)) fieldErrors.workActivities = 'Add at least one activity description';
            if (/Project is required/i.test(serverMessage)) fieldErrors.projectId = 'Project is required';
            if (/Date is required/i.test(serverMessage)) fieldErrors.date = 'Date is required';
          }
          if (Object.keys(fieldErrors).length) {
            setErrors((prev: any) => ({ ...prev, ...fieldErrors }));
          }
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(serverMessage);
      }

      const created = await response.json();
      setSuccess(true);

      // Determine created report ID from response shape
      const reportId = created?.data?._id || created?._id || created?.id;
      if (!reportId) {
        throw new Error('Created report ID not found');
      }

      // Redirect to daily report detail page after a short delay
      setTimeout(() => {
        navigate(`/daily-reports/${reportId}`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/daily-reports');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create Daily Report
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Daily report created successfully! Redirecting to report details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200, flex: 1 }} error={!!errors.projectId}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={formData.projectId}
                    label="Project *"
                    onChange={handleSelectChange('projectId')}
                    disabled={loadingProjects}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.projectId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.projectId}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange('date')}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />
              </Box>

              {/* Weather Information */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 150 }} error={!!errors.weather}>
                  <InputLabel>Weather</InputLabel>
                  <Select
                    value={formData.weather}
                    label="Weather"
                    onChange={handleSelectChange('weather')}
                    startAdornment={<CloudQueue sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    {weatherOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.weather && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.weather}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Temperature (°C)"
                  type="number"
                  value={formData.temperature}
                  onChange={handleInputChange('temperature')}
                  InputProps={{
                    startAdornment: <Thermostat sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 150 }}
                />

                <TextField
                  label="Total Workers"
                  type="number"
                  value={formData.totalWorkers}
                  onChange={handleInputChange('totalWorkers')}
                  error={!!errors.totalWorkers}
                  helperText={errors.totalWorkers}
                  sx={{ minWidth: 150 }}
                  required
                />
              </Box>

              {/* Work Activities */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment color="primary" />
                    Work Activities
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addWorkActivity}
                  >
                    Add Activity
                  </Button>
                </Box>

                {formData.workActivities.map((activity, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Activity {index + 1}</Typography>
                      {formData.workActivities.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeWorkActivity(index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Work Description"
                        multiline
                        rows={2}
                        value={activity.description}
                        onChange={(e) => handleWorkActivityChange(index, 'description', e.target.value)}
                        error={!!errors[`workActivity_${index}_description`]}
                        helperText={errors[`workActivity_${index}_description`]}
                        required
                      />
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Crew"
                          value={activity.crew}
                          onChange={(e) => handleWorkActivityChange(index, 'crew', e.target.value)}
                          error={!!errors[`workActivity_${index}_crew`]}
                          helperText={errors[`workActivity_${index}_crew`]}
                          sx={{ minWidth: 200, flex: 1 }}
                          required
                        />
                        
                        <TextField
                          label="Hours Worked"
                          type="number"
                          value={activity.hoursWorked}
                          onChange={(e) => handleWorkActivityChange(index, 'hoursWorked', parseFloat(e.target.value) || 0)}
                          sx={{ minWidth: 120 }}
                        />
                        
                        <TextField
                          label="Location"
                          value={activity.location}
                          onChange={(e) => handleWorkActivityChange(index, 'location', e.target.value)}
                          sx={{ minWidth: 200, flex: 1 }}
                        />
                      </Box>
                    </Stack>
                  </Card>
                ))}
              </Box>

              {/* Material Deliveries */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Material Deliveries</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addMaterialDelivery}
                  >
                    Add Delivery
                  </Button>
                </Box>

                {formData.materialDeliveries.map((delivery, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Delivery {index + 1}</Typography>
                      <IconButton
                          size="small"
                          onClick={() => removeMaterialDelivery(index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                    </Box>
                    
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Material"
                          value={delivery.material}
                          onChange={(e) => handleMaterialDeliveryChange(index, 'material', e.target.value)}
                          sx={{ minWidth: 200, flex: 1 }}
                        />
                        
                        <TextField
                          label="Quantity"
                          type="number"
                          value={delivery.quantity}
                          onChange={(e) => handleMaterialDeliveryChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          sx={{ minWidth: 120 }}
                        />
                        
                        <TextField
                          label="Unit"
                          value={delivery.unit}
                          onChange={(e) => handleMaterialDeliveryChange(index, 'unit', e.target.value)}
                          sx={{ minWidth: 100 }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Supplier"
                          value={delivery.supplier}
                          onChange={(e) => handleMaterialDeliveryChange(index, 'supplier', e.target.value)}
                          sx={{ minWidth: 200, flex: 1 }}
                        />
                        
                        <TextField
                          label="Delivery Time"
                          type="time"
                          value={delivery.deliveryTime}
                          onChange={(e) => handleMaterialDeliveryChange(index, 'deliveryTime', e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{ minWidth: 150 }}
                        />
                      </Box>
                    </Stack>
                  </Card>
                ))}
              </Box>

              {/* Notes */}
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Additional notes, observations, or comments..."
              />

              {/* Status */}
              <FormControl sx={{ maxWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange('status')}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Report'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DailyReportCreate;